import type { ChatUiHotelRoomsGroup, ChatUiHotelRoomsRate } from './hotel_rooms_schema.js';

/** O total da tarifa para efeito de ORDENAÇÃO. Tarifa sem preço vale
 *  `+Infinity` de propósito: ela desempata para o FIM da lista em vez de virar
 *  0 e se passar pela mais barata do grupo. */
function totalAmount(rate: ChatUiHotelRoomsRate): number {
  return rate.total?.amount ?? Number.POSITIVE_INFINITY;
}

/** Preço puro, do menor para o maior (#6914): nem fornecedor, nem política,
 *  nem reembolso, nem café, nem tarifa negociada mexem na ordem. O `sort` é
 *  estável, então empate de preço mantém a ordem de chegada. Ordenar é o
 *  ÚNICO uso: nada corta a cauda de tarifas — o cartão repassa 100% do que o
 *  fornecedor devolveu. */
function compareRates(a: ChatUiHotelRoomsRate, b: ChatUiHotelRoomsRate): number {
  return totalAmount(a) - totalAmount(b);
}

/** A tarifa que a linha do cartão mostra: a mais barata do grupo, a primeira
 *  na empate. */
export function bestRate(group: ChatUiHotelRoomsGroup): ChatUiHotelRoomsRate {
  const first = group.rates[0];
  // O schema garante `rates` com no mínimo 1 item; sem tarifa não existe "melhor".
  if (!first) throw new Error('hotel_rooms: grupo de quarto sem tarifas');
  return group.rates.reduce((best, rate) => (compareRates(rate, best) < 0 ? rate : best), first);
}

/** Todas as tarifas por preço (a de `bestRate` na frente). O card mostra a
 *  PRIMEIRA que passa nos chips ligados, então a ordem inteira importa, não só
 *  a cabeça: com ela, a tarifa à vista sob um recorte é a mais barata que o
 *  atende — a mesma que o destaque do agente escolhe (#6470). */
export function rankRates(rates: readonly ChatUiHotelRoomsRate[]): ChatUiHotelRoomsRate[] {
  return [...rates].sort(compareRates);
}

/** A ordem da lista: grupos pela tarifa mais barata de cada um. Não há menu
 *  "Priorizar" (lista curta e homogênea não tem o que ordenar — só filtro
 *  decide), então o rank roda uma vez e a ordem é fixa. Puro. */
export function rankGroups(groups: ChatUiHotelRoomsGroup[]): ChatUiHotelRoomsGroup[] {
  return [...groups].sort((a, b) => compareRates(bestRate(a), bestRate(b)));
}

/** O porão ("Outras opções", recolhido): o grupo cujo nome a taxonomia não
 *  leu (`unclassified`) e que não traz tarifa negociada. A tarifa de acordo
 *  nunca vai para lá, mesmo com nome ilegível (#5922: o fornecedor nomeia o
 *  quarto do acordo só com o nome da empresa; #6914: nenhuma tarifa do
 *  fornecedor Acordo fica escondida) — é a tarifa que o cliente quer que o
 *  viajante use. Regra ÚNICA: o cartão e a cena do agente separam o porão por
 *  ela, para os dois nunca divergirem. */
export function isOtherRoomsGroup(group: ChatUiHotelRoomsGroup): boolean {
  return group.unclassified === true && !group.rates.some((rate) => rate.negotiated);
}

/** Diária: com `total` E `nights` conhecidos, `total/nights` SEMPRE — é a única
 *  conta coerente com a linha do total (QA #0914, E2 hotel_rooms:cb8240e2:1:
 *  nights 6, total 761.5, totalPerNight 380.75 imprimia "380,75/noite · 6
 *  noites · 761,50", 380,75 × 6 ≠ 761,50). O `totalPerNight` do provider serve
 *  só quando falta um dos dois: sem `nights` (não dá pra dividir) ou sem
 *  `total` (nada pra dividir) — nesses casos ele é a única diária disponível.
 *  Sem nenhum dos dois, cai no total cru (mesmo que não seja diária de
 *  verdade — quem sabe dizer isso é `isNightlyPrice`, no agents
 *  `apps/services/agents/src/domains/hotel/ui/ui.ts`, que usa a MESMA conta). */
export function perNightOf(
  rate: ChatUiHotelRoomsRate,
  nights?: number,
): { amount: number; currency: string } | undefined {
  if (rate.total && nights && nights >= 1) {
    return { amount: rate.total.amount / nights, currency: rate.total.currency };
  }
  if (rate.totalPerNight) return rate.totalPerNight;
  return rate.total;
}
